import { Injectable } from '@angular/core';
import { List as ImmutableList } from 'immutable';
import { BehaviorSubject, Observable } from 'rxjs';
import { CompanyClassificationType, List } from '../constants-types';
import { Company, SupportInfo } from '../core-data-model';
import { Util } from '../core-utilities';
import { UserProfileSettingsService } from '../system-services';
import { CompaniesApi } from './CompaniesApi.connect';
import { LocalDemoAccountStoreService } from './local-demo-account-store/local-demo-account-store.service';

@Injectable({ providedIn: 'root' })
export class CompaniesDataService {
    companies: List<Company> = ImmutableList();
    companies$: Observable<List<Company>>;
    companiesPromise: Promise<List<Company>>;
    currentCompany: Company;

    private _companies = new BehaviorSubject<List<Company>>(this.companies);

    constructor(
        private companiesAPI: CompaniesApi,
        private util: Util,
        private userProfile: UserProfileSettingsService,
        private localDemoAccountStore: LocalDemoAccountStoreService
    ) {
        this.companies$ = this._companies.asObservable();
        this.companiesPromise = this.fetchCompanies();

        this.getCurrentLoggedInCompany().then((company) => {
            this.currentCompany = company;
        });
    }

    get currentCompanyName() {
        return this.currentCompany?.name;
    }

    get isSingleCompany(): boolean {
        return this.companies.size === 1;
    }

    get firstCompany(): Company {
        return this.companies ? this.companies.first() : null;
    }

    async getCurrentLoggedInCompany(): Promise<Company> {
        const listOfAvailableCompanies = await this.companiesPromise;
        return listOfAvailableCompanies.find(
            (company) => company.id === this.userProfile.currentCompany
        );
    }

    async getAccountSupportInfo(): Promise<SupportInfo> {
        return this.companiesAPI.getAccountSupportInfo();
    }

    async fetchCompanies(): Promise<List<Company>> {
        const [apiCompanies, localStorageCompanies] = await Promise.all([
            this.companiesAPI.getAllAccessibleCompanies(),
            this.localDemoAccountStore.getLocalStorageCompanies()
        ]);

        const preparedApiCompanies = apiCompanies.filter((apiCompany) => {
            const localStorageCompany = localStorageCompanies.find(
                (localCompany) => localCompany.id === apiCompany.id
            );

            return !localStorageCompany?.isDeleted;
        });

        const mergedCompanies = [
            ...preparedApiCompanies,
            ...localStorageCompanies.filter((x) => !x.isDeleted)
        ].filter(
            (company, index, self) =>
                index === self.findIndex((c) => c.id === company.id)
        );

        this.companies = ImmutableList(
            this.util.array.arraySortBy(mergedCompanies, 'name')
        );

        this._companies.next(this.companies);
        return this.companies;
    }

    async addCompany(
        companyGuid: string,
        companyName: string,
        companyClassificationType: CompanyClassificationType,
        deleted = false
    ) {
        await this.localDemoAccountStore.addCompanyToLocalStorage(
            companyGuid,
            companyName,
            companyClassificationType,
            deleted
        );
        await this.fetchCompanies();
    }

    async deleteCompany(
        companyGuid: string,
        companyName: string,
        companyClassificationType: CompanyClassificationType
    ) {
        await this.addCompany(
            companyGuid,
            companyName,
            companyClassificationType,
            true
        );
    }
}
